import { TFunction } from 'i18next';
import { KpiObjectType } from '@/api/entities/kpi';
import {
  KpiClassMetric,
  KpiModel,
  KpiModelSummary,
  KpiTier,
  KpiTierColor,
  PerClassBarMetric,
} from '@/type/kpi-dashboard.types';

/**
 * Light theme tier palette.
 * `solid` carries the graphic fills and `text` the labels, so every tier stays
 * readable on the white background of the page.
 */
export const KPI_TIER_COLORS: Record<KpiTier, KpiTierColor> = {
  strong: {
    solid: '#12B76A',
    text: '#067647',
    soft: '#ECFDF3',
    border: '#A6F4C5',
  },
  moderate: {
    solid: '#F79009',
    text: '#B54708',
    soft: '#FFFAEB',
    border: '#FEDF89',
  },
  weak: {
    solid: '#F94144',
    text: '#F94144',
    soft: '#FEF3F2',
    border: '#FECDCA',
  },
};

const STRONG_IOU_THRESHOLD = 0.75;
const MODERATE_IOU_THRESHOLD = 0.5;

export const resolveIouTier = (iou: number): KpiTier => {
  if (iou >= STRONG_IOU_THRESHOLD) return 'strong';
  if (iou >= MODERATE_IOU_THRESHOLD) return 'moderate';

  return 'weak';
};

const NICE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 8, 10];

/**
 * Rounds a chart domain up so it splits into `tickCount - 1` intervals of an
 * identical whole size, keeping the y axis labels on integers such as
 * 0 / 6 / 12 / 18 / 24. Bucket counts are always whole numbers, so a fractional
 * tick would never be meaningful here.
 */
export const getNiceAxisMax = (max: number, tickCount = 5): number => {
  const intervals = Math.max(tickCount - 1, 1);
  const minStep = Math.max(Math.ceil(max / intervals), 1);
  const magnitude = 10 ** Math.floor(Math.log10(minStep));
  const niceStep =
    NICE_STEPS.map((candidate) => candidate * magnitude).find(
      (candidate) => candidate >= minStep,
    ) ?? 10 * magnitude;

  return Math.ceil(niceStep) * intervals;
};

export const buildAxisTicks = (max: number, tickCount = 5): number[] => {
  const step = max / (tickCount - 1);

  return Array.from({ length: tickCount }, (_, index) =>
    Math.round(index * step),
  );
};

const IOU_BUCKET_COUNT = 10;

const IOU_BUCKET_LABELS = Array.from(
  { length: IOU_BUCKET_COUNT },
  (_, index) =>
    `${(index / IOU_BUCKET_COUNT).toFixed(1)}–${((index + 1) / IOU_BUCKET_COUNT).toFixed(1)}`,
);

/**
 * Histogram buckets take their colour from their lower bound: below 0.5 is
 * weak, below 0.7 moderate, and the rest strong.
 */
const resolveBucketTier = (lowerBound: number): KpiTier => {
  if (lowerBound >= 0.7) return 'strong';
  if (lowerBound >= 0.5) return 'moderate';

  return 'weak';
};

/**
 * A class is flagged as rare when it holds less than this share of the busiest
 * class of its model, which is what tints its annotation bar.
 */
const RARE_ANNOTATION_RATIO = 0.1;

export const isRareAnnotationCount = (
  annotations: number,
  maxAnnotations: number,
): boolean => annotations < maxAnnotations * RARE_ANNOTATION_RATIO;

const RANKING_SIZE = 5;

/**
 * Derives every aggregate the dashboard shows from the per-class metrics, so
 * the mean IoU, the tier counts, the histogram and the rankings can never drift
 * apart from the data they summarise.
 */
/**
 * A class with no annotations was never measured, so its IoU of 0 is an absence
 * of data rather than a bad score. Anything that reads as a score leaves those
 * classes out; only the annotations chart, which is about volume, keeps them.
 */
export const hasAnnotations = (kpiClass?: KpiClassMetric): boolean =>
  (kpiClass?.annotations ?? 0) > 0;

export const getKpiModelSummary = (model: KpiModel): KpiModelSummary => {
  const classes = (model?.classes ?? []).filter(hasAnnotations);
  const classCount = classes?.length ?? 0;

  const iouSum = classes.reduce((total, { iou }) => total + (iou ?? 0), 0);

  const tierCounts: Record<KpiTier, number> = {
    strong: 0,
    moderate: 0,
    weak: 0,
  };
  const bucketCounts = new Array<number>(IOU_BUCKET_COUNT).fill(0);

  classes.forEach(({ iou }) => {
    const score = iou ?? 0;

    tierCounts[resolveIouTier(score)] += 1;
    bucketCounts[
      Math.min(Math.floor(score * IOU_BUCKET_COUNT), IOU_BUCKET_COUNT - 1)
    ] += 1;
  });

  // `classes` arrives sorted by ascending IoU, and both sorts below are stable,
  // so tied classes keep the order of the source export in either direction.
  const bestFirst = [...classes].sort(
    (first, second) => (second?.iou ?? 0) - (first?.iou ?? 0),
  );

  return {
    classCount,
    mIou: classCount ? Number((iouSum / classCount).toFixed(3)) : 0,
    tierCounts,
    distribution: bucketCounts.map((count, index) => ({
      label: IOU_BUCKET_LABELS[index],
      count,
      tier: resolveBucketTier(index / IOU_BUCKET_COUNT),
    })),
    topClasses: bestFirst.slice(0, RANKING_SIZE),
    flopClasses: classes.slice(0, RANKING_SIZE),
  };
};

/**
 * Resolves the noun a page labels its rows with: the KPI pages talk about
 * equipments or damages rather than the generic "class" the collection stores.
 * Returned lowercase for use inside sentences.
 */
export const getKpiObjectLabels = (
  t: TFunction<undefined>,
  type: KpiObjectType,
) => {
  const isEquipment = type === KpiObjectType.EQUIPMENT;

  return {
    object: t(isEquipment ? 'common:equipment' : 'common:damage').toLowerCase(),
    objects: t(
      isEquipment ? 'common:equipments' : 'common:damages',
    ).toLowerCase(),
  };
};

/**
 * Counted labels read as singular up to one ("0 damage", "1 equipment") and as
 * plural above that. French takes the singular for zero too, so the same rule
 * holds in both locales.
 */
export const isSingularCount = (count: number): boolean => (count ?? 0) <= 1;

/** Picks the singular or plural object noun for a counted label. */
export const getKpiObjectLabel = (
  t: TFunction<undefined>,
  type: KpiObjectType,
  count: number,
): string => {
  const { object, objects } = getKpiObjectLabels(t, type);

  return isSingularCount(count) ? object : objects;
};

/**
 * A perfect score needs no decimals to be unambiguous, so both IoU formats
 * below render it as a bare "1".
 */
const formatIou = (value: number | undefined, decimals: number): string => {
  const score = value ?? 0;

  return score === 1 ? '1' : score.toFixed(decimals);
};

/**
 * IoU on the axis, where the ticks are round quarters and a compact label keeps
 * the axis readable once it narrows: two decimals, and an exact zero as "0".
 */
export const formatIouScore = (value?: number): string =>
  formatIou(value, value ? 2 : 0);

/**
 * IoU in a tooltip, to three decimals. Scores are stored at that precision, so
 * a fixed width keeps them comparable from one bar to the next and never rounds
 * two distinct classes to the same figure.
 */
export const formatPreciseIouScore = (value?: number): string =>
  formatIou(value, 3);

export const formatAnnotationCount = (value?: number): string =>
  (value ?? 0).toLocaleString('fr-FR');

/**
 * Both per-class charts show the same two figures on hover, whichever metric
 * their bars happen to plot, so the tooltip reads the same on either tab.
 */
export const buildPerClassBarMetrics = (
  t: TFunction<undefined>,
  kpiClass: KpiClassMetric,
): PerClassBarMetric[] => [
  { label: t('kpi:scoreIou'), value: formatPreciseIouScore(kpiClass?.iou) },
  {
    label: t(
      isSingularCount(kpiClass?.annotations)
        ? 'kpi:annotation'
        : 'kpi:annotations',
    ),
    value: formatAnnotationCount(kpiClass?.annotations),
  },
];

/**
 * Room a single axis label needs before it starts touching its neighbour.
 */
const MIN_AXIS_LABEL_WIDTH = 46;

/**
 * How many entries to skip between axis labels so they stop overlapping once
 * the chart narrows. The step is rounded up to one that divides the axis
 * evenly, so the last entry always keeps its label rather than the axis
 * trailing off mid-range.
 */
export const getAxisLabelStep = (
  labelCount: number,
  availableWidth: number,
  minLabelWidth: number = MIN_AXIS_LABEL_WIDTH,
): number => {
  const lastIndex = labelCount - 1;

  if (lastIndex < 1 || !availableWidth) return 1;

  const minStep = Math.max(
    1,
    Math.ceil((minLabelWidth * labelCount) / availableWidth),
  );

  for (let step = minStep; step <= lastIndex; step += 1) {
    if (lastIndex % step === 0) return step;
  }

  return lastIndex;
};
